import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  SigeBlock, PIKTOGRAMME, LegendeEintrag,
  piktoPfad, piktoPfadLokal, aufloesePikto, istCodeAktiv, entferneCode, isoTyp, ISO_LABELS,
} from './types';
import { ALLE_PIKTO_CODES } from './piktoManifest';
import { MUSTER_LEGENDE, MUSTER_LMRA_FAKTOREN, MUSTER_LMRA_REGELN } from './musterVorlage';
import { supabase } from '../../services/supabaseClient';
import { X, Save, Plus, Trash2, Loader2, Settings2, Search, Upload, Star, Edit } from 'lucide-react';
import { Button } from '../Button';

const PIKTO_BUCKET = 'sige-sicherheitszeichen';
// Dateiname im Bucket → ISO-Code, z. B. "ISO_7010_M014.png" → "M014"
const ISO_FILE_RE = /^ISO_7010_([PMWEF]\d{3}[A-Za-z]?)\.(png|svg)$/i;

// Prefix-Filter für das Zeichen-Grid
const TYP_FILTER: { key: string; label: string }[] = [
  { key: 'all', label: 'Alle' },
  { key: 'M', label: 'Gebot' },
  { key: 'W', label: 'Warnung' },
  { key: 'P', label: 'Verbot' },
  { key: 'E', label: 'Rettung' },
  { key: 'F', label: 'Brandschutz' },
];

const TYP_RING: Record<string, string> = {
  gebot: 'ring-blue-500/30', warnung: 'ring-yellow-500/40', verbot: 'ring-red-500/30',
  rettung: 'ring-green-500/30', brandschutz: 'ring-red-500/30',
};

interface BlockEditorProps {
  block: SigeBlock;
  onClose: () => void;
  onSaved: (block: SigeBlock) => void;
}

const PIKTO_FARBEN: Record<string, string> = {
  gebot: 'bg-blue-600',
  warnung: 'bg-yellow-400 text-black',
  verbot: 'bg-red-600',
  rettung: 'bg-green-600',
  brandschutz: 'bg-red-600',
};

// Zeigt das echte ISO-7010-SVG; fällt bei fehlendem Bild auf gefärbte Form + Kürzel zurück.
const PiktoBild: React.FC<{ iso?: string; typ: string; symbol: string; small?: boolean }> = ({ iso, typ, symbol, small }) => {
  const [stufe, setStufe] = useState(0); // 0 = Bucket-PNG, 1 = lokales PNG, 2 = Ersatzform
  const cls = small ? 'w-5 h-5' : 'w-8 h-8';
  if (iso && stufe < 2) {
    return (
      <img
        src={stufe === 0 ? piktoPfad(iso) : piktoPfadLokal(iso)}
        alt={iso}
        onError={() => setStufe(s => s + 1)}
        className={`${cls} object-contain shrink-0`}
      />
    );
  }
  const eckig = typ === 'warnung';
  return (
    <span
      className={`${cls} rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${PIKTO_FARBEN[typ] || 'bg-slate-500'} ${eckig ? '!rounded-none' : ''}`}
      style={eckig ? { clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' } : undefined}
    >
      {symbol}
    </span>
  );
};

export const BlockEditor: React.FC<BlockEditorProps> = ({ block, onClose, onSaved }) => {
  const inhalt = (block.inhalt && typeof block.inhalt === 'object') ? block.inhalt : {};

  const [icons, setIcons] = useState<string[]>(inhalt.icons || []);
  const [eintraege, setEintraege] = useState<LegendeEintrag[]>(
    inhalt.eintraege?.length ? inhalt.eintraege : (block.typ === 'legende' ? MUSTER_LEGENDE : [])
  );
  const defaultZeilen = (): string[] => {
    if (inhalt.zeilen?.length) return inhalt.zeilen;
    if (block.typ === 'risk_assessment') return [...MUSTER_LMRA_FAKTOREN, '', ...MUSTER_LMRA_REGELN];
    if (typeof inhalt === 'object' && Object.keys(inhalt).length && !inhalt.icons && !inhalt.eintraege) {
      return Object.entries(inhalt).map(([k, v]) => `${k}: ${v}`);
    }
    return [];
  };
  const [zeilenText, setZeilenText] = useState(defaultZeilen().join('\n'));
  const [neuKuerzel, setNeuKuerzel] = useState('');
  const [neuBedeutung, setNeuBedeutung] = useState('');
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Wählbare ISO-Codes: statisches Manifest als Basis + dynamisch aus dem Bucket
  // (falls SELECT-Policy vorhanden) + gerade hochgeladene. So sehen alle Nutzer global
  // hochgeladene Zeichen. Bilder kommen per öffentlicher URL aus dem Bucket (piktoPfad).
  const [dynCodes, setDynCodes] = useState<string[]>([]);
  const [favoriten, setFavoriten] = useState<{ code: string; label: string | null }[]>([]);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [tempLabel, setTempLabel] = useState('');
  const [typFilter, setTypFilter] = useState('all');
  const [suche, setSuche] = useState('');
  const [upBusy, setUpBusy] = useState(false);
  const [upMsg, setUpMsg] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const getPiktoLabel = (code: string) => {
    const fav = favoriten.find(f => f.code === code);
    if (fav && fav.label) return fav.label;
    return ISO_LABELS[code] || code;
  };

  const saveLabel = async (code: string, neuesLabel: string) => {
    if (!neuesLabel.trim()) {
      setEditingCode(null);
      return;
    }
    setFavoriten(prev => {
      const existiert = prev.some(f => f.code === code);
      if (existiert) {
        return prev.map(f => f.code === code ? { ...f, label: neuesLabel } : f);
      } else {
        return [...prev, { code, label: neuesLabel }];
      }
    });
    setEditingCode(null);

    try {
      const { error } = await supabase.from('sige_pikto_favoriten').upsert({
        code,
        label: neuesLabel,
        sortierung: favoriten.findIndex(f => f.code === code) !== -1 ? favoriten.findIndex(f => f.code === code) + 1 : 99
      }, { onConflict: 'code' });
      if (error) throw error;
    } catch (err) {
      console.error('Umbenennen in DB fehlgeschlagen', err);
    }
  };

  const bucketCodes = useMemo(
    () => Array.from(new Set([...ALLE_PIKTO_CODES, ...dynCodes])).sort(),
    [dynCodes]
  );

  // Schnellauswahl aus DB-Favoriten; Fallback = die bisherigen 12 Presets, falls Tabelle leer.
  const schnell = useMemo(() => (
    favoriten.length
      ? favoriten.map(f => ({ code: f.code, label: f.label || ISO_LABELS[f.code] || f.code, typ: isoTyp(f.code), symbol: f.code[0] }))
      : PIKTOGRAMME.map(p => ({ code: p.iso || p.key, label: p.label, typ: p.typ, symbol: p.symbol }))
  ), [favoriten]);

  // Dynamische Bucket-Liste (nur wenn SELECT-Policy erlaubt) + globale Favoriten laden.
  useEffect(() => {
    if (block.typ !== 'piktogrammfeld') return;
    let aktiv = true;
    supabase.storage.from(PIKTO_BUCKET).list('', { limit: 1000 })
      .then(({ data }) => {
        if (!aktiv || !data) return;
        const codes = data
          .map(f => f.name.match(ISO_FILE_RE)?.[1]?.toUpperCase())
          .filter((c): c is string => !!c);
        if (codes.length) setDynCodes(prev => Array.from(new Set([...prev, ...codes])));
      })
      .catch(() => { /* keine Listen-Policy → Manifest reicht */ });
    supabase.from('sige_pikto_favoriten').select('code,label,sortierung').order('sortierung')
      .then(({ data }) => { if (aktiv && data) setFavoriten(data.map(d => ({ code: d.code, label: d.label }))); })
      .catch(() => { /* Tabelle fehlt → Fallback auf Presets */ });
    return () => { aktiv = false; };
  }, [block.typ]);

  const istFavorit = (code: string) => favoriten.some(f => f.code === code);

  // Favorit global umschalten (insert/delete in sige_pikto_favoriten).
  const toggleFavorit = async (code: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const drin = istFavorit(code);
    setFavoriten(prev => drin ? prev.filter(f => f.code !== code) : [...prev, { code, label: ISO_LABELS[code] || code }]);
    try {
      if (drin) {
        await supabase.from('sige_pikto_favoriten').delete().eq('code', code);
      } else {
        await supabase.from('sige_pikto_favoriten').insert({ code, label: ISO_LABELS[code] || code });
      }
    } catch (err) {
      console.error('Favorit speichern fehlgeschlagen', err);
      setFavoriten(prev => drin ? [...prev, { code, label: ISO_LABELS[code] || code }] : prev.filter(f => f.code !== code));
    }
  };

  // Toggle per ISO-Code (dedupliziert auch Legacy-Keys, die auf denselben Code zeigen)
  const toggleCode = (code: string) => {
    setIcons(istCodeAktiv(icons, code) ? entferneCode(icons, code) : [...icons, code]);
  };

  // Neues Zeichen hochladen → landet global im Bucket und ist sofort wählbar.
  const handleUpload = async (file: File) => {
    setUpMsg(null);
    const treffer = file.name.match(/([PMWEF]\d{3}[A-Za-z]?)/i);
    const code = treffer?.[1]?.toUpperCase();
    if (!code) {
      setUpMsg('Dateiname muss den ISO-Code enthalten, z. B. ISO_7010_M001.png, M001.png, oder .svg.');
      return;
    }
    const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
    setUpBusy(true);
    try {
      const { error } = await supabase.storage.from(PIKTO_BUCKET)
        .upload(`ISO_7010_${code}.${ext}`, file, { upsert: true, contentType: file.type || (ext === 'svg' ? 'image/svg+xml' : 'image/png') });
      if (error) throw error;
      setDynCodes(prev => Array.from(new Set([...prev, code])));
      if (!istCodeAktiv(icons, code)) setIcons(prev => [...prev, code]);
      setTypFilter('all'); setSuche(code);
      setUpMsg(`„${code}" hochgeladen, global verfügbar und ausgewählt.`);
    } catch (e) {
      console.error('Piktogramm-Upload fehlgeschlagen', e);
      setUpMsg('Upload fehlgeschlagen. Vermutlich fehlt die Storage-INSERT-Policy (siehe Hinweis).');
    } finally {
      setUpBusy(false);
    }
  };

  // Alle Codes im Grid zeigen (auch Favoriten) — sonst fehlen z. B. die M-Zeichen unter „Gebot".
  const gefiltert = bucketCodes
    .filter(c => typFilter === 'all' || c[0]?.toUpperCase() === typFilter)
    .filter(c => {
      if (!suche.trim()) return true;
      const q = suche.trim().toLowerCase();
      return c.toLowerCase().includes(q) || getPiktoLabel(c).toLowerCase().includes(q);
    });

  const buildInhalt = (): any => {
    if (block.typ === 'piktogrammfeld') return { icons };
    if (block.typ === 'legende') return { eintraege };
    return { zeilen: zeilenText.split('\n').filter(l => l.trim().length > 0 || l === '') };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data, error } = await supabase.from('sige_bloecke')
        .update({ inhalt: buildInhalt() }).eq('id', block.id).select().single();
      if (error) throw error;
      if (data) onSaved(data as SigeBlock);
      onClose();
    } catch (e) {
      console.error('Block speichern fehlgeschlagen', e);
      setFehler('Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'text-sm p-2 border border-slate-200 rounded outline-none focus:ring-2 focus:ring-blue-500/20';

  return (
    <div className="fixed inset-0 z-[170] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-slate-500" /> {block.titel}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {block.typ === 'piktogrammfeld' && (
            <div>
              {/* Schnellauswahl: globale Favoriten aus der DB (★ im Grid pflegbar) */}
              <p className="text-sm font-medium text-slate-600 mb-2">Schnellauswahl (Favoriten)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
                {schnell.map(p => {
                  const aktiv = istCodeAktiv(icons, p.code);
                  const isEditing = editingCode === p.code;
                  return (
                    <div
                      key={p.code}
                      className={`group relative flex items-center gap-2 p-2 rounded-lg border text-left text-xs transition-all cursor-pointer ${
                        aktiv ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-500/20' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      onClick={() => {
                        if (!isEditing) toggleCode(p.code);
                      }}
                    >
                      <PiktoBild iso={p.code} typ={p.typ} symbol={p.symbol} />
                      {isEditing ? (
                        <input
                          type="text"
                          value={tempLabel}
                          onChange={e => setTempLabel(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              saveLabel(p.code, tempLabel);
                            } else if (e.key === 'Escape') {
                              setEditingCode(null);
                            }
                          }}
                          onBlur={() => saveLabel(p.code, tempLabel)}
                          className="flex-1 min-w-0 bg-white border border-slate-300 rounded px-1.5 py-0.5 text-slate-800 focus:outline-none focus:border-blue-500"
                          autoFocus
                          onClick={e => e.stopPropagation()}
                        />
                      ) : (
                        <span className="flex-1 truncate pr-5" title={p.label}>
                          {p.label}
                        </span>
                      )}
                      {!isEditing && (
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            setEditingCode(p.code);
                            setTempLabel(p.label);
                          }}
                          className="absolute right-1.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 p-1 text-slate-400 hover:text-slate-600 transition-opacity"
                          title="Name umbenennen"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Vollständige ISO-7010-Bibliothek aus dem Bucket */}
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-sm font-medium text-slate-600">Alle Sicherheitszeichen ({bucketCodes.length})</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".png,image/png,.svg,image/svg+xml"
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f);
                    e.target.value = '';
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={upBusy}
                  title="Fehlendes ISO-7010-Zeichen als SVG hochladen (global verfügbar)"
                >
                  {upBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  Zeichen hochladen
                </Button>
              </div>
              {upMsg && <p className="text-xs text-slate-500 mb-2">{upMsg}</p>}

              {bucketCodes.length === 0 ? (
                <p className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-lg p-3">
                  Keine Zeichen im Manifest. Erzeuge „piktoManifest.ts" neu aus
                  public/sicherheitszeichen/iso7010/.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {TYP_FILTER.map(f => (
                      <button
                        key={f.key}
                        onClick={() => setTypFilter(f.key)}
                        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                          typFilter === f.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <div className="relative mb-3">
                    <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      className={inputCls + ' w-full pl-8'}
                      placeholder="Suchen (Code oder Bezeichnung, z. B. M014 oder Helm)…"
                      value={suche}
                      onChange={e => setSuche(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
                    {gefiltert.map(code => {
                      const aktiv = istCodeAktiv(icons, code);
                      const typ = isoTyp(code);
                      const fav = istFavorit(code);
                      return (
                        <div
                          key={code}
                          onClick={() => toggleCode(code)}
                          title={getPiktoLabel(code)}
                          className={`relative flex flex-col items-center gap-1 p-2 rounded-lg border text-center transition-all cursor-pointer ${
                            aktiv ? `border-blue-400 bg-blue-50 ring-1 ${TYP_RING[typ]}` : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={e => toggleFavorit(code, e)}
                            title={fav ? 'Favorit entfernen' : 'Als Favorit (Schnellauswahl)'}
                            className="absolute top-1 right-1 p-0.5 text-slate-300 hover:text-yellow-500"
                          >
                            <Star className={`w-3.5 h-3.5 ${fav ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                          </button>
                          <PiktoBild iso={code} typ={typ} symbol={code[0]} />
                          <span className="text-[11px] leading-tight text-slate-600">{code}</span>
                        </div>
                      );
                    })}
                    {gefiltert.length === 0 && (
                      <p className="col-span-full text-xs text-slate-400 py-2">Keine Treffer.</p>
                    )}
                  </div>
                </>
              )}

              {/* Aktuelle Auswahl (auch Zeichen, die im Filter gerade nicht sichtbar sind) */}
              {icons.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-2">Ausgewählt ({icons.length}):</p>
                  <div className="flex flex-wrap gap-1.5">
                    {icons.map(entry => {
                      const def = aufloesePikto(entry);
                      if (!def) return null;
                      return (
                        <button
                          key={entry}
                          onClick={() => def.iso ? toggleCode(def.iso) : setIcons(icons.filter(i => i !== entry))}
                          title={`${def.label}: entfernen`}
                          className="flex items-center gap-1 pl-1 pr-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-xs hover:border-red-300 hover:bg-red-50"
                        >
                          <PiktoBild iso={def.iso} typ={def.typ} symbol={def.symbol} small />
                          <span className="text-slate-600">{def.iso || def.label}</span>
                          <X className="w-3 h-3 text-slate-400" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {block.typ === 'legende' && (
            <div>
              <p className="text-sm text-slate-500 mb-3">Abkürzungen / Legende (vorbefüllt aus Muster):</p>
              <div className="space-y-1 mb-3">
                {eintraege.map((e, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input className={inputCls + ' w-32'} value={e.kuerzel}
                      onChange={ev => setEintraege(eintraege.map((x, j) => j === i ? { ...x, kuerzel: ev.target.value } : x))} />
                    <input className={inputCls + ' flex-1'} value={e.bedeutung}
                      onChange={ev => setEintraege(eintraege.map((x, j) => j === i ? { ...x, bedeutung: ev.target.value } : x))} />
                    <button onClick={() => setEintraege(eintraege.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input className={inputCls + ' w-32'} placeholder="Kürzel" value={neuKuerzel} onChange={e => setNeuKuerzel(e.target.value)} />
                <input className={inputCls + ' flex-1'} placeholder="Bedeutung" value={neuBedeutung} onChange={e => setNeuBedeutung(e.target.value)} />
                <Button variant="outline" size="sm" onClick={() => {
                  if (!neuKuerzel.trim()) return;
                  setEintraege([...eintraege, { kuerzel: neuKuerzel.trim(), bedeutung: neuBedeutung.trim() }]);
                  setNeuKuerzel(''); setNeuBedeutung('');
                }}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
          )}

          {block.typ !== 'piktogrammfeld' && block.typ !== 'legende' && (
            <div>
              <p className="text-sm text-slate-500 mb-2">
                Inhalt (eine Zeile pro Eintrag{block.typ === 'risk_assessment' ? ', vorbefüllt aus Muster-LMRA' : ''}):
              </p>
              <textarea
                className="w-full h-72 text-sm p-3 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500/20 font-mono"
                value={zeilenText}
                onChange={e => setZeilenText(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end items-center gap-3">
          {fehler && <span className="text-xs text-red-600 mr-auto">{fehler}</span>}
          <Button variant="ghost" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button data-tutorial="sigeplan.block-speichern" variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
};
